import React, { useState, useEffect } from 'react';
import api from '../api/axiosConfig';
import Modal from './Modal';
import EditTopicForm from './EditTopicForm';
import './TopicManager.css'; // We'll style this next

const TopicManager = () => {
  const [topics, setTopics] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [newTopic, setNewTopic] = useState({
    name: '',
    description: '',
    subjectId: localStorage.getItem('tm-lastSubject') || '',
    order: 1,
    icon: 'star', // Default icon
    minMasteryScore: 80 // Default passing grade
  });
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, topicRes] = await Promise.all([
        api.get('/admin/subjects')
      ]);
      setSubjects(subRes.data);
    } catch (err) { console.error("Error loading topics"); }
  };

  useEffect(() => {
    if (newTopic.subjectId) {
      fetchTopics(newTopic.subjectId);
    }
  }, [newTopic.subjectId]);

  const fetchTopics = async (subjectId) => {
    try {
      const res = await api.get(`/admin/topics?subjectId=${subjectId}`);
      setTopics(res.data);
    } catch (err) { console.error("Error loading topics"); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/topics', newTopic);
      setNewTopic(prev => ({ ...prev, name: '', description: '', order: 1 }));
      fetchTopics(newTopic.subjectId);
    } catch (err) { alert("Failed to add topic"); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this topic?")) {
      await api.delete(`/admin/topics/${id}`);
      setTopics(topics.filter(t => t.id !== id));
    }
  };

  const handleEdit = (id) => {
    setIsEditModalOpen(true);
    const topicToEdit = topics.find(t => t.id === id);
    setSelectedTopic(topicToEdit);
  };

  const onSubjectSelected = (e) => {
    const subjectId = e.target.value;
    localStorage.setItem('tm-lastSubject', subjectId);
    setNewTopic(prev => ({ ...prev, subjectId: subjectId })); // Set subject for new topic
  };

  const handleSaveTopic = async (updatedData) => { 
    setIsSaving(true);
    try {
      await api.put(`/admin/topics/${selectedTopic.id}`, updatedData);
      setIsEditModalOpen(false);
      fetchTopics(selectedTopic.subjectId); // Refresh topics for the same subject
    } catch (err) {
      console.error("Failed to save topic:", err);
      alert("Failed to save topic. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="topic-container">
      <form onSubmit={handleSubmit} className="topic-form">
        <h3>Create New Topic</h3>

        <input
          placeholder="Topic Title (e.g. Intro to Algebra)"
          value={newTopic.name}
          onChange={e => setNewTopic({ ...newTopic, name: e.target.value })}
          required
        />

        <select
          value={newTopic.subjectId}
          onChange={onSubjectSelected}
          required
        >
          <option value="">Select Subject</option>
          {subjects.map(s => (
            <option key={s.id} value={s.id}>{s.name} ({s.Grade?.name})</option>
          ))}
        </select>

        <input
          type="number"
          placeholder="Order"
          value={newTopic.order}
          onChange={e => setNewTopic({ ...newTopic, order: e.target.value })}
        />

        <textarea
          placeholder="Brief description of what students will learn..."
          value={newTopic.description}
          onChange={e => setNewTopic({ ...newTopic, description: e.target.value })}
        />

        <button type="submit" className="submit-btn">Add Topic to Curriculum</button>
      </form>

      <div className="topic-list">
        {/* Table goes here */}
        <table>
          <thead>
            <tr>
              <th style={{ width: '80px' }}>Order</th>
              <th>Topic Details</th>
              <th>Type</th>
              <th>Hierarchy</th>
              <th>Questions</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {topics.length > 0 ? (
              topics.map((t) => (
                <tr key={t.id}>
                  <td><span style={{ fontWeight: 'bold' }}>#{t.order}</span></td>
                  <td className="topic-col">
                    <div style={{ fontWeight: '600' }}>{t.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{t.description}</div>
                  </td>
                  <td>
                    <span style={{ background: '#dbeafe', padding: '2px 8px', borderRadius: '12px', fontSize: '0.8rem' }}>
                      {t.topicType}
                    </span>
                  </td>
                  <td>
                    <span className="badge-subject">{t.Subject?.name}</span>
                    <span className="badge-grade">{t.Subject?.Grade?.name}</span>
                  </td>
                  <td style={{ fontWeight: '600', color: '#64748b' }}>{t.questionCount}</td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                      <button onClick={() => handleDelete(t.id)} className="delete-text">Delete</button>
                      <button onClick={() => handleEdit(t.id)} className="delete-text">Edit</button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>No topics found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Topic"
      >
        <EditTopicForm
          topic={selectedTopic}
          onSubmit={handleSaveTopic}
          onCancel={() => setIsEditModalOpen(false)}
          isSubmitting={isSaving}
        />
      </Modal>
    </div>
  );
};

export default TopicManager;